const KEY = 'auth_return_to';

// '/' simples, mas não '//host' nem '/\host' (protocol-relative → open redirect).
const isInternalPath = (path: string) =>
  path.startsWith('/') && !path.startsWith('//') && !path.startsWith('/\\');

// Só caminhos internos; nunca rotas públicas (loop nas telas de auth,
// destino sem sentido nas demais). Espelha os `isProtected: false` de
// router/routes.ts — não dá pra importar de lá (ciclo via api/client);
// o teste guarda-trilho em returnTo.test.ts acusa drift. Aplicado no save
// E no consume: um valor plantado direto no sessionStorage também não passa.
const isSafeTarget = (path: string) =>
  isInternalPath(path) &&
  !path.startsWith('/login') &&
  !path.startsWith('/auth/callback') &&
  !path.startsWith('/first-access') &&
  !path.startsWith('/forgot-password') &&
  !path.startsWith('/q/') &&
  // Telas do 2º fator (MFA/passkey) e aviso de privacidade: públicas no
  // registry — salvar uma delas como destino levaria o login de volta ao
  // desafio (loop) ou a uma página sem sessão.
  !path.startsWith('/mfa-challenge') &&
  !path.startsWith('/mfa-enrollment') &&
  !path.startsWith('/passkey-enrollment') &&
  !path.startsWith('/privacidade') &&
  // Protegida, mas destino sem sentido: 401 no access-denied não deve
  // trazer o usuário de volta pra cá após o login.
  !path.startsWith('/access-denied');

/**
 * Guarda a rota de origem pra retomar após o login. sessionStorage porque
 * o fluxo passa por hard navigation (401 do interceptor, OAuth round-trip),
 * que perde o state do react-router.
 */
export const saveReturnTo = (path?: string | null) => {
  if (path && isSafeTarget(path)) {
    sessionStorage.setItem(KEY, path);
  }
};

/** Descarta a rota salva sem consumir (ex.: logout explícito). */
export const clearReturnTo = () => sessionStorage.removeItem(KEY);

/** Lê e limpa a rota salva (uso único). */
export const consumeReturnTo = (): string | null => {
  const path = sessionStorage.getItem(KEY);
  sessionStorage.removeItem(KEY);
  return path && isSafeTarget(path) ? path : null;
};

/**
 * Resolve o destino pós-login: prefere o state.from do router, senão o salvo
 * em sessionStorage, senão '/' (que delega ao RedirectToDefaultRoute). SEMPRE
 * consome o salvo, mesmo quando o state.from vence — senão o destino sobra no
 * sessionStorage e vaza pro próximo login da mesma aba.
 * Seguro pra navigate(); nunca passar o resultado pra redirectTo/window.location.
 */
/**
 * Destino após um evento de CREDENCIAL (login por senha, reset de senha).
 *
 * Diferença única em relação ao `resolveReturnTo`: o valor guardado em
 * sessionStorage é consumido mas NÃO honrado. Ele existe para retomar de onde o
 * usuário estava quando levou um 401 (`services/api/client.ts:42`), e sobrevive
 * a um login abandonado na mesma aba — aí o login seguinte caía em `/perfil` ou
 * `/dashboard` em vez do painel, que é a tela default do sistema.
 *
 * `state.from` continua vencendo: ele vem do redirect REAL desta navegação (o
 * ProtectedRoute manda para /login quem tentou abrir uma rota protegida), e
 * ignorá-lo mandaria a pessoa para o painel em vez da rota que ela pediu.
 *
 * Devolve `'/'`, não `/painel-aduaneiro` cravado: quem decide é o
 * `RedirectToDefaultRoute`, que leva ao painel quando a feature está ligada
 * (é o caso de todos hoje) e desce a cadeia quando não está. Hardcodar o painel
 * poria quem não tem a feature no loop que `ProtectedRoute.tsx:141` documenta.
 *
 * CUSTO ACEITO: quem levar um 401 e logar por senha não volta mais para a tela
 * onde estava — cai no painel. O round-trip do OAuth (`AuthCallback`) e o
 * primeiro acesso seguem usando o `resolveReturnTo`, porque neles o
 * sessionStorage é o único carregador do destino (hard navigation).
 */
export const resolveAfterCredential = (stateFrom?: string | null): string => {
  // Consome mesmo sem honrar: senão o destino sobra e vaza pro próximo login
  // da mesma aba — exatamente o bug que esta função existe para matar.
  consumeReturnTo();
  return (stateFrom && isSafeTarget(stateFrom) ? stateFrom : null) || '/';
};

export const resolveReturnTo = (stateFrom?: string | null): string => {
  const stored = consumeReturnTo();
  const fromState = stateFrom && isSafeTarget(stateFrom) ? stateFrom : null;
  return fromState || stored || '/';
};
